import { Chip, FormHelperText, Grid2 as Grid } from "@mui/material";
import { useDisabledInput } from "@sinch/hooks/utilities/UseDisabledInput";
import { isNil } from "ramda";
import React, { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumberInput } from "@sinch/components";
import { usePriority } from "@sinch/components/application/common/usePriority";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import { CheckboxInput } from "@sinch/components/form/CheckboxInput/CheckboxInput";

export interface PriorityInputProps {
  value: number | null;
  onChange: (val: number | null) => void;
}

export const PriorityInput: FC<PriorityInputProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { getPriorityOptions, getPriorityLabel, isNamedPriority } = usePriority();
  const [selectValue, setSelectValue] = useState<string | number | null>(
    isNamedPriority(value ?? 0) ? value : "custom"
  );

  const [disabled, setDisabled, handleChange] = useDisabledInput<number | null>({
    initDisabled: isNil(value),
    initialValue: 0,
    onChange: onChange,
    emptyValue: null,
  });

  const priorities = [...getPriorityOptions(), { value: "custom", label: t("custom") }];

  return (
    <>
      <Grid alignItems="center" container direction="row" spacing={2} wrap="nowrap">
        <Grid size="auto">
          <CheckboxInput
            data-cy="IntervalSelect-priorityInterval-checkbox"
            FormControlProps={{ fullWidth: false }}
            label={t("RateBuilder.priorityUnit")}
            onChange={(e) => setDisabled(!e.target.checked)}
            value={!disabled}
          />
        </Grid>
        <Grid container size="grow">
          <Grid size="auto" sx={{ minWidth: "130px" }}>
            <AutocompleteInput<{ label: string; value: string | number; color: string }, false, true, false>
              data-cy="IntervalSelect-priorityInterval-select"
              disableClearable
              disabled={disabled}
              fullWidth
              getOptionLabel={(option) =>
                isNamedPriority(option.value) ? getPriorityLabel(option.value) : t("custom")
              }
              inputReadOnly
              onChange={(newValue) => {
                if (newValue !== "custom") {
                  onChange(+(newValue ?? 0));
                }
                setSelectValue(newValue);
              }}
              options={priorities}
              paperWidth="fit-content"
              renderOption={(props, option) => (
                <li {...props} style={{ margin: 0 }}>
                  <Chip
                    clickable={false}
                    label={isNamedPriority(option.value) ? `${option.label} (${option.value})` : option.label}
                    size="small"
                    sx={({ palette }) => ({
                      backgroundColor: option.color,
                      width: "100%",
                      color: option.color && palette.getContrastText(option.color),
                      "&:hover": {
                        backgroundColor: option.color,
                      },
                    })}
                  />
                </li>
              )}
              slotProps={{
                popper: {
                  sx: { width: "fit-content", minWidth: "fit-content" },
                },
              }}
              value={selectValue ?? 0}
            />
          </Grid>

          <Grid size={1} sx={{ minWidth: "80px" }}>
            <NumberInput
              data-cy="IntervalSelect-priorityInterval-customInput"
              disabled={disabled || selectValue !== "custom"}
              fullWidth={false}
              min={0}
              onChange={(newValue) => handleChange(newValue)}
              value={value ?? 0}
            />
          </Grid>
        </Grid>
      </Grid>
      <FormHelperText sx={{ pl: 1 }}>
        {t("RateBuilder.whenMultiplePriorityUnitsOverlapsDescription")}
      </FormHelperText>
    </>
  );
};
